/**
 * The list filter a server condition group reaches (`{operator, conditions}`
 * with `{field, operator, value}` each, as list_shaping approves it), as the
 * list's own filter conditions for Clu's apply-filters command.
 */
import { asRecord } from '@/components/agent-chat/as-record.util';
import type { AgentFilterCondition } from '@/store/features/ui.feature';

function textOf(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/**
 * A condition's value as the list filter's text. Clu sends a list for "is any
 * of" (`["LL-0036", "LL-0037"]`), and the list table lowercases the value, so
 * a list here would throw and take the page down: it joins with ", ", which
 * "is any of" splits on. Other values read as text; none reads as "".
 */
export function filterValueText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) {
    return value
      .filter((item) => item !== undefined && item !== null)
      .map((item) => String(item).trim())
      .filter(Boolean)
      .join(', ');
  }
  return typeof value === 'object' ? '' : String(value);
}

/**
 * Clu's apply-filters result (`{list_id, filters: [{field, operator, value,
 * logic}]}`) as the list's filter command; undefined without a list. A
 * condition missing its field or operator is dropped.
 */
export function agentFilterCommandOf(
  result: unknown
): { listId: string; filters: AgentFilterCondition[] } | undefined {
  const record = asRecord(result) ?? {};
  const listId = textOf(record.list_id);
  if (!listId) return undefined;
  const raw = Array.isArray(record.filters) ? record.filters : [];
  const filters: AgentFilterCondition[] = [];
  raw.forEach((item, index) => {
    const condition = asRecord(item) ?? {};
    const field = textOf(condition.field);
    const operator = textOf(condition.operator);
    if (!field || !operator) return;
    const logic =
      condition.logic === 'or' || condition.logic === 'and'
        ? condition.logic
        : undefined;
    filters.push({
      id: textOf(condition.id) ?? String(index + 1),
      field,
      operator,
      value: filterValueText(condition.value),
      ...(logic ? { logic } : {}),
    });
  });
  return { listId, filters };
}

/** A server group of conditions (`{operator, conditions}`) as list filters. */
export function listFiltersOf(
  conditions: unknown
): AgentFilterCondition[] | undefined {
  const group = asRecord(conditions) ?? {};
  const raw = Array.isArray(group.conditions) ? group.conditions : null;
  if (!raw || raw.length === 0) return undefined;
  const logic =
    String(group.operator ?? '').toUpperCase() === 'OR' ? 'or' : 'and';
  const filters: AgentFilterCondition[] = [];
  raw.forEach((item, index) => {
    const condition = asRecord(item) ?? {};
    const field = textOf(condition.field);
    const operator = textOf(condition.operator);
    if (!field || !operator) return;
    filters.push({
      id: String(index + 1),
      field,
      operator,
      value: filterValueText(condition.value),
      logic,
    });
  });
  return filters.length > 0 ? filters : undefined;
}
